// utils/apiError.js
//
// Enterprise-grade API error normalisation for user-facing surfaces.
//
// Goals:
//  - Show the user a clear, calm, actionable message — never raw backend/infra internals
//    (e.g. an S3 "Access Denied / Status Code: 403" leaking through a generic
//    `request_processing_exception`).
//  - Preserve a correlation/reference id so users can quote it to support.
//  - Log the full technical detail to the console (hook up to monitoring later) for engineers.

// Friendly copy for known backend error codes.
const FRIENDLY_BY_CODE = {
  validation_exception: 'Some details look incorrect. Please review and try again.',
  invalid_request: 'Some details look incorrect. Please review and try again.',
  unauthorized: 'Your session has expired. Please sign in again.',
  forbidden: 'You don’t have permission to perform this action.',
  not_found: 'We couldn’t find what you were looking for. Please refresh and try again.',
  duplicate_resource: 'This already exists. Please review and try again.',
};

// Backend strings that are too generic / internal to ever show verbatim.
const GENERIC_OR_INTERNAL = [
  'error while processing the request',
  'internal server error',
  'access denied',
  'null',
  'unexpected',
  'exception',
];

function looksInternal(msg) {
  if (!msg) return true;
  const m = String(msg).toLowerCase();
  return GENERIC_OR_INTERNAL.some((g) => m.includes(g));
}

/**
 * Normalise any axios/fetch error into a user-safe message + support reference.
 * @param {unknown} err
 * @param {{ fallback?: string, action?: string }} [opts]
 *   - action: a verb phrase describing what the user was doing, e.g. "upload your Aadhaar".
 *     Used to phrase opaque server errors as "We couldn’t <action> right now…".
 * @returns {{ message: string, correlationId: (string|null), code: (string|null), status: (number|null) }}
 */
export function friendlyApiError(err, { fallback = 'Something went wrong. Please try again.', action } = {}) {
  // Always log full technical context for engineers (never surfaced to the user).
  try {
    // eslint-disable-next-line no-console
    console.error('[apiError]', action || '', err?.response?.status, err?.response?.data || err?.message, err);
  } catch (_) { /* noop */ }

  // No HTTP response → network / timeout / CORS.
  if (!err?.response) {
    const isTimeout = err?.code === 'ECONNABORTED';
    return {
      message: isTimeout
        ? 'This is taking longer than expected. Please check your connection and try again.'
        : 'Network error — please check your connection and try again.',
      correlationId: null,
      code: err?.code || 'network_error',
      status: null,
    };
  }

  const { status, data = {} } = err.response;
  const code = data.code || null;
  const correlationId = data.correlation_id || data.correlationId || null;

  let message = FRIENDLY_BY_CODE[code];

  if (!message) {
    if (status === 401) message = 'Your session has expired. Please sign in again.';
    else if (status === 403 && code !== 'request_processing_exception') message = 'You don’t have permission to perform this action.';
    else if (status === 413) message = 'That file is too large. Please upload a smaller file.';
    else if (status === 415) message = 'That file type isn’t supported. Please upload a PDF, JPG or PNG.';
    else if (status === 429) message = 'Too many attempts. Please wait a moment and try again.';
    else if (status >= 500) message = 'Our servers ran into a problem. Please try again in a moment.';
  }

  // Opaque processing failures (e.g. an S3 upload error wrapped as request_processing_exception):
  // phrase around what the user was doing instead of exposing infra details.
  if (!message && action) message = `We couldn’t ${action} right now. Please try again in a moment.`;

  // Only fall back to the backend's own message if it's specific and safe to show.
  if (!message) message = looksInternal(data.message) ? fallback : data.message;

  return { message, correlationId, code, status };
}

export default friendlyApiError;
